import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { Send, CheckCircle } from 'lucide-react';
import { sendLeadEmail } from '../services/emailService';

// Declare gtag function for Google Analytics
/* global gtag */

const LeadForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    // Add UTM parameters if available
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source') || 'direct';
    const utmCampaign = urlParams.get('utm_campaign') || 'organic';
    
    const formData = {
      ...data,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      timestamp: new Date().toISOString()
    };

    try {
      // Send email using Brevo API
      await sendLeadEmail(formData);
      
      console.log('Form submitted and email sent successfully:', formData);
      
      // Push event to GTM
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'lead_form_submitted' });
      
      // Google Ads conversion tracking
      if (typeof gtag !== 'undefined') {
        gtag('event', 'conversion', {
          'send_to': 'AW-17432744490/bWVRCPX69KIbEKqkyvhA',
          'value': 1.0,
          'currency': 'INR'
        });
      }
      
      setIsSubmitted(true);
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="lead-form" className="section" style={{ background: 'white' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="card"
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #0ea5e9'
            }}
          >
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: '#1e293b',
              marginBottom: '16px'
            }}>
              Thanks — We've Received Your Request
            </h2>
            
            <p style={{
              fontSize: '1.125rem',
              color: '#64748b',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              Great — we'll email a 30-minute planning slot within 24 hours. Meanwhile, prepare: product images, product descriptions, and shipping details.
            </p>
            
            <a 
              href="https://in.bigin.online/org60042158995/bookings/digital-marketing-and-technogy-sales#/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Book a time now
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-form" className="section" style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      position: 'relative',
      overflow: 'hidden',
      paddingTop: '100px',
      paddingBottom: '120px' // Add bottom padding to prevent overlap with floating CTA
    }}>
      {/* Animated background elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        animation: 'float 20s ease-in-out infinite'
      }} />
      
      <div className="container" style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
            style={{
              display: 'inline-block',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              padding: '8px 24px',
              marginBottom: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <span style={{
              color: 'white',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              🚀 Limited Time Offer
            </span>
          </motion.div>
          
          <h2 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '800',
            color: 'white',
            marginBottom: '24px',
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            lineHeight: '1.1'
          }}>
            Get Your E-Commerce Website
            <span style={{
              background: 'linear-gradient(45deg, #ffd700, #ffed4e)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'block',
              marginTop: '8px'
            }}>
              in Just 15 Days!
            </span>
          </h2>
          
          <p style={{
            fontSize: '1.5rem',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '700px',
            margin: '0 auto 32px',
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
            lineHeight: '1.6'
          }}>
            Ready to transform your business idea into a profitable online store?
          </p>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '16px',
            marginBottom: '40px'
          }}>
            {['✅ Production ready', '✅ 30-day support', '✅ Fast loading', '✅ Mobile optimized'].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '25px',
                  padding: '8px 16px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {item}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
            className="card"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2), 0 8px 32px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Form header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '32px',
              paddingBottom: '24px',
              borderBottom: '2px solid rgba(102, 126, 234, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.75rem',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '12px'
              }}>
                Get a free consultation!
              </h3>
              <p style={{
                color: '#64748b',
                fontSize: '1.1rem',
                margin: 0
              }}>
                We'll reply within 24 hours with a launch plan and pricing
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  {...register('fullName', { required: 'Full name is required' })}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                    {errors.fullName.message}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  className="form-input"
                  {...register('phone', { required: 'Phone number is required' })}
                  placeholder="Enter your phone number"
                />
                {errors.phone && (
                  <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                    {errors.phone.message}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  {...register('email', { 
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  placeholder="your@business.com (optional)"
                />
                {errors.email && (
                  <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Quick Brief</label>
                <textarea
                  className="form-textarea"
                  {...register('brief')}
                  placeholder="Tell us about your business and goals... (optional)"
                  rows={4}
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  background: isSubmitting 
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)' 
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: '700',
                  padding: '20px 32px',
                  borderRadius: '16px',
                  border: 'none',
                  boxShadow: isSubmitting 
                    ? '0 4px 15px rgba(156, 163, 175, 0.3)' 
                    : '0 8px 30px rgba(102, 126, 234, 0.4)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {isSubmitting ? (
                  <>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid transparent',
                      borderTop: '3px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '12px'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    🚀 Submit Request
                    <Send size={24} style={{ marginLeft: '12px' }} />
                  </>
                )}
              </motion.button>
            </form>
            
            {/* Description inside form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
              style={{
                textAlign: 'center',
                marginTop: '24px',
                padding: '20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
              }}
            >
              <p style={{
                fontSize: '1rem',
                color: '#64748b',
                margin: 0,
                lineHeight: '1.6'
              }}>
                Get a <strong>conversion-focused e-commerce store</strong> with fast-loading designs, 
                product upload support, and 30-day launch assistance.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
        }
      `}</style>
    </section>
  );
};

export default LeadForm;
